import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import ConfirmDeleteDialog from '../../components/admin/ConfirmDeleteDialog';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import {
  api,
  type AudienceFilter,
  type EmailTemplate,
  type EmailTemplateInput,
  type TriggerType,
} from '../../lib/api';
import { C2Button } from '../../components/c2';

interface VariablesPayload {
  variables: Array<{ key: string; label: string; example: string }>;
  triggerTypes: TriggerType[];
  audienceFilters: AudienceFilter[];
}

const TRIGGER_LABELS: Record<TriggerType, string> = {
  on_signup: 'On signup — fires immediately when user is created',
  days_after_signup: 'Days after signup — fires N days after the user joins',
  when_no_extension: 'When no extension — fires after N days if extension not installed',
  when_no_links: "When no links — fires after N days if they haven't made any links",
  when_link_count: 'When link count — fires once they have at least N links',
  manual: 'Manual only — only sent via the Send test button or other manual path',
};

const TRIGGER_NEEDS_PARAM: Record<TriggerType, boolean> = {
  on_signup: false,
  days_after_signup: true,
  when_no_extension: false,
  when_no_links: false,
  when_link_count: true,
  manual: false,
};

const TRIGGER_PARAM_LABEL: Record<TriggerType, string> = {
  on_signup: '',
  days_after_signup: 'Days after signup',
  when_no_extension: 'Minimum days (optional, default 1)',
  when_no_links: 'Minimum days (optional, default 1)',
  when_link_count: 'Minimum link count',
  manual: '',
};

const AUDIENCE_LABELS: Record<AudienceFilter, string> = {
  all: 'All users',
  personal_only: 'Personal accounts only',
  team_only: 'Team workspaces only',
};

const EMPTY: EmailTemplateInput = {
  key: '',
  name: '',
  subject: '',
  body_html: '',
  trigger_type: 'manual',
  trigger_param: null,
  audience_filter: 'all',
  enabled: true,
  send_once: true,
};

export default function AdminEmailTemplateEditPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';

  const [vars, setVars] = useState<VariablesPayload | null>(null);
  const [form, setForm] = useState<EmailTemplateInput>(EMPTY);
  const [existing, setExisting] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [testSendOpen, setTestSendOpen] = useState(false);

  useEffect(() => {
    if (!gate.ready) return;
    api
      .superEmailTemplateVariables()
      .then(setVars)
      .catch(err => setError((err as Error).message));
    if (!isNew) {
      api
        .superEmailTemplate(id!)
        .then(t => {
          setExisting(t);
          setForm({
            key: t.key,
            name: t.name,
            subject: t.subject,
            body_html: t.body_html,
            trigger_type: t.trigger_type,
            trigger_param: t.trigger_param,
            audience_filter: t.audience_filter,
            enabled: t.enabled,
            send_once: t.send_once,
          });
          setLoading(false);
        })
        .catch(err => {
          setError((err as Error).message);
          setLoading(false);
        });
    }
  }, [gate.ready, id, isNew]);

  if (!gate.ready) return null;

  const update = <K extends keyof EmailTemplateInput>(k: K, v: EmailTemplateInput[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await api.superCreateEmailTemplate(form);
        navigate(`/admin/emails/${created.id}`, { replace: true });
      } else {
        const updated = await api.superUpdateEmailTemplate(id!, form);
        setExisting(updated);
      }
    } catch (err) {
      setError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const p = await api.superPreviewEmailTemplate({
        subject: form.subject,
        body_html: form.body_html,
      });
      setPreview({ subject: p.subject, html: p.html });
    } catch (err) {
      setError((err as Error).message || 'Preview failed');
      setPreviewing(false);
    }
  };

  const handleDelete = async () => {
    await api.superDeleteEmailTemplate(id!);
    navigate('/admin/emails', { replace: true });
  };

  if (loading) {
    return (
      <AdminShell title="Loading…">
        <div className="text-ink-soft" style={{ padding: 40 }}>Loading…</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={isNew ? 'New template' : existing?.name || 'Edit template'}
      subtitle={
        isNew
          ? 'Create a new email template with a trigger rule.'
          : existing
          ? `Key: ${existing.key} · ${existing.trigger_summary}`
          : undefined
      }
      rightSlot={
        <Link
          to="/admin/emails"
          className="text-[13px] font-semibold no-underline"
          style={{ color: 'var(--color-ink-soft)' }}
        >
          ← All templates
        </Link>
      }
    >
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '1fr 320px', gap: 24 }}>
        {/* Form */}
        <div
          className="bg-surface"
          style={{
            padding: 24,
            border: '1px solid var(--color-hairline)',
            borderRadius: 16,
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Key" hint={!isNew ? 'Editable, but must remain unique' : 'Lowercase, letters/numbers/underscore'}>
              <input
                value={form.key}
                onChange={e => update('key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="w-full font-mono outline-none"
                style={inputStyle}
                placeholder="welcome_v2"
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Field label="Display name">
              <input
                value={form.name}
                onChange={e => update('name', e.target.value)}
                className="w-full outline-none"
                style={inputStyle}
                placeholder="Welcome (revised)"
              />
            </Field>
          </div>

          <Field label="Subject">
            <input
              value={form.subject}
              onChange={e => update('subject', e.target.value)}
              className="w-full outline-none"
              style={inputStyle}
              placeholder="Welcome to Tidy Go Links, {{firstName}}"
            />
          </Field>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Trigger">
              <select
                value={form.trigger_type}
                onChange={e => update('trigger_type', e.target.value as TriggerType)}
                className="w-full outline-none"
                style={inputStyle}
              >
                {(vars?.triggerTypes ?? Object.keys(TRIGGER_LABELS) as TriggerType[]).map(t => (
                  <option key={t} value={t}>
                    {TRIGGER_LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Audience">
              <select
                value={form.audience_filter}
                onChange={e => update('audience_filter', e.target.value as AudienceFilter)}
                className="w-full outline-none"
                style={inputStyle}
              >
                {(vars?.audienceFilters ?? ['all', 'personal_only', 'team_only'] as AudienceFilter[]).map(a => (
                  <option key={a} value={a}>
                    {AUDIENCE_LABELS[a]}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {TRIGGER_PARAM_LABEL[form.trigger_type] && (
            <Field label={TRIGGER_PARAM_LABEL[form.trigger_type]}>
              <input
                value={form.trigger_param ?? ''}
                onChange={e => update('trigger_param', e.target.value.replace(/[^0-9]/g, '') || null)}
                className="w-full font-mono outline-none"
                style={inputStyle}
                placeholder={TRIGGER_NEEDS_PARAM[form.trigger_type] ? 'Required' : 'Optional'}
                inputMode="numeric"
              />
            </Field>
          )}

          <div className="flex gap-4" style={{ margin: '12px 0' }}>
            <Toggle
              label="Enabled"
              checked={form.enabled ?? true}
              onChange={v => update('enabled', v)}
            />
            <Toggle
              label="Send once per user"
              checked={form.send_once ?? true}
              onChange={v => update('send_once', v)}
            />
          </div>

          <Field label="Body HTML">
            <textarea
              value={form.body_html}
              onChange={e => update('body_html', e.target.value)}
              className="w-full font-mono outline-none"
              style={{ ...inputStyle, minHeight: 420, lineHeight: 1.45, resize: 'vertical' }}
              placeholder={'<p>Hiya {{firstName}},</p>\n<p>Welcome to Tidy Go Links!</p>'}
              spellCheck={false}
            />
          </Field>

          <div className="flex items-center justify-between gap-2 mt-4">
            <div className="flex gap-2">
              {!isNew && (
                <C2Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDeleteOpen(true)}
                  style={{ color: 'var(--color-peach-deep)' }}
                >
                  Delete
                </C2Button>
              )}
            </div>
            <div className="flex gap-2">
              <C2Button variant="outline" size="sm" onClick={() => { void handlePreview(); }} disabled={previewing}>
                Preview
              </C2Button>
              {!isNew && (
                <C2Button variant="outline" size="sm" onClick={() => setTestSendOpen(true)}>
                  Send test
                </C2Button>
              )}
              <C2Button variant="accent" size="sm" onClick={() => { void save(); }} disabled={saving}>
                {saving ? 'Saving…' : isNew ? 'Create template' : 'Save changes'}
              </C2Button>
            </div>
          </div>
        </div>

        {/* Variables sidebar */}
        <VariablesSidebar vars={vars} />
      </div>

      {/* Preview modal */}
      {preview && (
        <PreviewModal
          subject={preview.subject}
          html={preview.html}
          onClose={() => { setPreview(null); setPreviewing(false); }}
        />
      )}

      {/* Send test modal */}
      {testSendOpen && existing && (
        <SendTestModal
          templateId={existing.id}
          defaultEmail={gate.user.email}
          onClose={() => setTestSendOpen(false)}
        />
      )}

      {/* Delete confirmation */}
      {existing && (
        <ConfirmDeleteDialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          title="Delete template"
          description={`This will permanently delete the "${existing.name}" template (${existing.key}).`}
          confirmPhrase={existing.key}
          consequences={[
            'Template removed from the email_templates table',
            'Cron will stop firing this template',
            'No effect on emails already sent — those are still logged in onboarding_emails',
            'Not reversible',
          ]}
          onConfirm={handleDelete}
        />
      )}
    </AdminShell>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  border: '1px solid var(--color-hairline-strong)',
  borderRadius: 12,
  fontSize: 14,
  background: 'var(--color-surface)',
  color: 'var(--color-ink)',
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[12.5px] font-semibold text-ink">{label}</label>
        {hint && <span className="text-[11px] text-ink-muted">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="text-[13px] font-medium text-ink">{label}</span>
    </label>
  );
}

function VariablesSidebar({ vars }: { vars: VariablesPayload | null }) {
  return (
    <div
      className="bg-surface"
      style={{
        padding: 20,
        border: '1px solid var(--color-hairline)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-sm)',
        position: 'sticky',
        top: 100,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
      }}
    >
      <h3 className="font-display font-bold text-ink m-0 mb-2" style={{ fontSize: 15 }}>
        Variables
      </h3>
      <p className="text-xs text-ink-muted m-0 mb-3">
        Use these in subject or body. HTML-escaped on render.
      </p>
      {!vars ? (
        <div className="text-ink-soft text-sm">Loading…</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {vars.variables.map(v => (
            <div key={v.key}>
              <code
                className="font-mono"
                style={{
                  fontSize: 11.5,
                  padding: '2px 6px',
                  background: 'var(--color-bg-alt)',
                  border: '1px solid var(--color-hairline)',
                  borderRadius: 6,
                  color: 'var(--accent-deep)',
                  fontWeight: 600,
                }}
              >
                {`{{${v.key}}}`}
              </code>
              <div className="text-[11.5px] text-ink-muted mt-1">{v.label}</div>
              <div className="text-[10.5px] text-ink-faint" style={{ fontStyle: 'italic' }}>
                e.g. {v.example}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewModal({
  subject,
  html,
  onClose,
}: {
  subject: string;
  html: string;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(20, 18, 35, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-surface"
        style={{
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-ink m-0" style={{ fontSize: 20 }}>
            Preview
          </h2>
          <button
            onClick={onClose}
            className="bg-transparent border-0 cursor-pointer text-ink-muted"
            style={{ fontSize: 20 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--color-ink-muted)',
            paddingBottom: 8,
            borderBottom: '1px solid var(--color-hairline)',
            marginBottom: 12,
          }}
        >
          <b>Subject:</b> {subject}
        </div>
        <iframe
          srcDoc={html}
          title="Email preview"
          sandbox=""
          style={{
            flex: 1,
            border: '1px solid var(--color-hairline)',
            borderRadius: 12,
            minHeight: 400,
            width: '100%',
            background: '#fff',
          }}
        />
      </div>
    </div>
  );
}

function SendTestModal({
  templateId,
  defaultEmail,
  onClose,
}: {
  templateId: number;
  defaultEmail: string;
  onClose: () => void;
}) {
  const [toEmail, setToEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await api.superTestSendEmailTemplate(templateId, toEmail);
      setDone(`Sent to ${toEmail}`);
    } catch (err) {
      setError((err as Error).message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(20, 18, 35, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-surface"
        style={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          padding: 28,
        }}
      >
        <h2 className="font-display font-bold text-ink m-0 mb-2" style={{ fontSize: 20 }}>
          Send test email
        </h2>
        <p className="text-sm text-ink-soft m-0 mb-3">
          Renders the template with your own user context (or sample defaults) and sends through
          Resend. Doesn't update the send-once log.
        </p>
        <label className="text-[12.5px] font-semibold text-ink block mb-1.5">
          Recipient email
        </label>
        <input
          value={toEmail}
          onChange={e => setToEmail(e.target.value)}
          disabled={busy}
          className="w-full outline-none"
          style={inputStyle}
        />
        {error && (
          <div
            className="mt-3 text-[13px]"
            style={{
              color: 'var(--color-peach-deep)',
              background: 'var(--color-peach)',
              padding: '8px 12px',
              borderRadius: 10,
            }}
          >
            {error}
          </div>
        )}
        {done && (
          <div
            className="mt-3 text-[13px]"
            style={{
              color: 'var(--color-mint-deep)',
              background: 'var(--color-mint)',
              padding: '8px 12px',
              borderRadius: 10,
            }}
          >
            {done}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <C2Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Close
          </C2Button>
          <C2Button variant="accent" size="sm" onClick={() => { void send(); }} disabled={busy || !toEmail}>
            {busy ? 'Sending…' : 'Send'}
          </C2Button>
        </div>
      </div>
    </div>
  );
}
